import { IconChevronLeft } from '../components/talkAppleIcons';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { SKIP_API } from '../apiMode';
import { isGuestUser } from '../constants/auth';
import { CATEGORIES } from '../data/categories';
import { BASE_CSS_VARS } from '../theme/appearance';
import DeleteAccountPanel from '../components/DeleteAccountPanel';
import RestorePurchasesPanel from '../components/RestorePurchasesPanel';

const SETTINGS_CSS = `
.settings-page button:focus-visible, .settings-page a:focus-visible, .settings-page summary:focus-visible { outline: 2px solid var(--primary-dark); outline-offset: 3px; }
.settings-page summary { cursor: pointer; list-style: none; min-height: 48px; display: flex; align-items: center; gap: 12px; color: var(--text); font-size: 15px; }
.settings-page summary::-webkit-details-marker { display: none; }
.settings-page summary::after { content: '›'; color: var(--text-secondary); font-size: 23px; margin-left: 4px; }
.settings-page details[open] > summary::after { transform: rotate(90deg); }
.settings-page summary > span:first-child { flex: 1; }
.settings-page button:disabled { opacity: .5; cursor: default; }
.settings-page .rp-btn { border-radius: 8px !important; box-shadow: none !important; animation: none !important; transform: none !important; transition: none !important; font-weight: 500 !important; }
.settings-page .rp-msg, .settings-page .rp-shake { animation: none !important; }
@media (prefers-reduced-motion: reduce) { .settings-page * { transition: none !important; } }
`;

export default function Settings() {
  const navigate = useNavigate();
  const { backgroundId, setBackgroundId, presets } = useTheme();
  const { user, login, register, logout, refreshMe } = useAuth();
  const premiumActive = user && Number(user.talk_premium) === 1;
  const isGuest = isGuestUser(user);

  const [appearOnline, setAppearOnline] = useState(
    () => localStorage.getItem('appear_online') !== 'false'
  );

  const [editingUsername, setEditingUsername] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [usernameSaving, setUsernameSaving] = useState(false);

  const startEditUsername = () => {
    setNewUsername(user?.username || '');
    setUsernameError('');
    setEditingUsername(true);
  };

  const handleUsernameChange = async (e) => {
    e.preventDefault();
    const trimmed = newUsername.trim();
    if (!trimmed || trimmed === user?.username) { setEditingUsername(false); return; }
    setUsernameSaving(true);
    setUsernameError('');
    try {
      const axios = (await import('axios')).default;
      await axios.put('/api/users/me', { username: trimmed });
      await refreshMe();
      setEditingUsername(false);
    } catch (err) {
      setUsernameError(err?.response?.data?.error || '変更に失敗しました');
    } finally {
      setUsernameSaving(false);
    }
  };

  const [authMode, setAuthMode] = useState('login'); // 'login' | 'register'
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authUsername, setAuthUsername] = useState('');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authTermsAccepted, setAuthTermsAccepted] = useState(false);

  const [interests, setInterests] = useState([]);
  const [interestsSaving, setInterestsSaving] = useState(false);
  const [interestsMsg, setInterestsMsg] = useState('');

  useEffect(() => {
    if (user?.interests) setInterests(user.interests);
  }, [user?.interests]);

  const toggleInterest = (id) => {
    setInterestsMsg('');
    setInterests((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 5) return prev;
      return [...prev, id];
    });
  };

  const saveInterests = async () => {
    if (SKIP_API) return;
    setInterestsSaving(true);
    setInterestsMsg('');
    try {
      const axios = (await import('axios')).default;
      await axios.put('/api/users/me/interests', { interests });
      await refreshMe();
      setInterestsMsg('興味タグを保存しました');
    } catch (err) {
      setInterestsMsg(err?.response?.data?.error || '保存に失敗しました');
    } finally {
      setInterestsSaving(false);
    }
  };

  const handleToggleOnline = () => {
    const next = !appearOnline;
    setAppearOnline(next);
    localStorage.setItem('appear_online', String(next));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    if (!authTermsAccepted) {
      setAuthError('続行するには利用規約（EULA）への同意が必要です');
      return;
    }
    setAuthLoading(true);
    try {
      await login(authEmail.trim(), authPassword);
      setAuthEmail(''); setAuthPassword('');
    } catch (err) {
      setAuthError(err?.response?.data?.error || 'ログインに失敗しました');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setAuthError('');
    if (!authTermsAccepted) {
      setAuthError('続行するには利用規約（EULA）への同意が必要です');
      return;
    }
    setAuthLoading(true);
    try {
      await register({
        username: authUsername.trim(),
        email: authEmail.trim(),
        password: authPassword,
        accepted_terms: authTermsAccepted,
      });
      setAuthEmail(''); setAuthPassword(''); setAuthUsername('');
    } catch (err) {
      setAuthError(err?.response?.data?.error || '登録に失敗しました');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  // ── 安全とプライバシー（App Store Guideline 1.2） ──
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [myReports, setMyReports] = useState([]);
  const [safetyLoaded, setSafetyLoaded] = useState(false);
  const [unblockingId, setUnblockingId] = useState(null);

  useEffect(() => {
    if (SKIP_API || !user) return;
    let alive = true;
    (async () => {
      const axios = (await import('axios')).default;
      const [blocked, reports] = await Promise.all([
        axios.get('/api/users/me/blocked').then(r => r.data).catch(() => []),
        axios.get('/api/reports/mine').then(r => r.data).catch(() => []),
      ]);
      if (!alive) return;
      setBlockedUsers(Array.isArray(blocked) ? blocked : []);
      setMyReports(Array.isArray(reports) ? reports : []);
      setSafetyLoaded(true);
    })();
    return () => { alive = false; };
  }, [user]);

  const unblockUser = async (target) => {
    if (!window.confirm(`${target.username} さんのブロックを解除しますか？`)) return;
    setUnblockingId(target.id);
    try {
      const axios = (await import('axios')).default;
      await axios.delete(`/api/users/${target.id}/block`);
      setBlockedUsers(list => list.filter(u => String(u.id) !== String(target.id)));
    } catch (_) {
      window.alert('ブロックの解除に失敗しました。時間をおいて再度お試しください。');
    } finally {
      setUnblockingId(null);
    }
  };

  return (
    <div className="settings-page" style={S.page}>
      <style>{SETTINGS_CSS}</style>
      <header style={S.header}>
        <button data-control="icon" data-control-id="Settings-back" type="button" style={S.back} onClick={() => navigate(-1)} aria-label="戻る"><IconChevronLeft /></button>
        <h1 style={S.title}>設定</h1>
        <span style={S.headerSpacer} />
      </header>

      {!SKIP_API && (
        <section style={S.section}>
          <h2 style={S.sectionTitle}>アカウント</h2>

          {!isGuest ? (
            <div style={S.accountInfo}>
              <div style={S.accountMeta}>
                {editingUsername ? (
                  <form onSubmit={handleUsernameChange} style={S.unameForm}>
                    <input
                      aria-label="ユーザー名"
                      style={S.unameInput}
                      value={newUsername}
                      onChange={e => setNewUsername(e.target.value)}
                      maxLength={32}
                      autoFocus
                    />
                    <button data-control="primary" data-control-id="Settings-unameSave" type="submit" style={S.unameSave} disabled={usernameSaving}>
                      {usernameSaving ? '保存中…' : '変更を保存'}
                    </button>
                    <button data-control="quiet" data-control-id="Settings-unameCancel" type="button" style={S.unameCancel} onClick={() => setEditingUsername(false)}>キャンセル</button>
                  </form>
                ) : (
                  <div style={S.unameRow}>
                    <span style={S.accountName}>{user?.username}</span>
                    <button data-control="text" data-control-id="Settings-unameEditBtn" type="button" style={S.unameEditBtn} onClick={startEditUsername}>名前を変更</button>
                  </div>
                )}
                {usernameError && <span style={S.unameError}>{usernameError}</span>}
                <span style={S.accountEmail}>{user?.email}</span>
              </div>
            </div>
          ) : (
            <>
              <p style={S.guestNote}>ゲストとして利用中です。登録またはログインするとデータが保持されます。</p>
              <div style={S.authTabs}>
                <button data-control="tab" data-control-id="Settings-authTab" type="button" aria-pressed={authMode === 'login'} style={{ ...S.authTab, ...(authMode === 'login' ? S.authTabActive : {}) }} onClick={() => { setAuthMode('login'); setAuthError(''); }}>ログイン</button>
                <button data-control="tab" data-control-id="Settings-authTab" type="button" aria-pressed={authMode === 'register'} style={{ ...S.authTab, ...(authMode === 'register' ? S.authTabActive : {}) }} onClick={() => { setAuthMode('register'); setAuthError(''); }}>新規登録</button>
              </div>
              <form onSubmit={authMode === 'login' ? handleLogin : handleRegister} style={S.authForm}>
                {authMode === 'register' && (
                  <input
                    style={S.authInput}
                    type="text"
                    placeholder="ユーザー名"
                    value={authUsername}
                    onChange={(e) => setAuthUsername(e.target.value)}
                    required
                    autoComplete="username"
                  />
                )}
                <input
                  style={S.authInput}
                  type="email"
                  placeholder="メールアドレス"
                  value={authEmail}
                  onChange={(e) => setAuthEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
                <input
                  style={S.authInput}
                  type="password"
                  placeholder="パスワード"
                  value={authPassword}
                  onChange={(e) => setAuthPassword(e.target.value)}
                  required
                  autoComplete={authMode === 'login' ? 'current-password' : 'new-password'}
                />
                {authError && <p style={S.authError}>{authError}</p>}
                <div style={S.authTermsCheck}>
                  <input
                    type="checkbox"
                    aria-label="利用規約とプライバシーポリシーに同意する"
                    checked={authTermsAccepted}
                    onChange={e => setAuthTermsAccepted(e.target.checked)}
                    style={S.authTermsCheckInput}
                  />
                  <span>
                    <button data-control="text" data-control-id="Settings-authLegalLink" type="button" style={S.authLegalLink} onClick={() => navigate('/terms')}>利用規約（EULA）</button>
                    と
                    <button data-control="text" data-control-id="Settings-authLegalLink" type="button" style={S.authLegalLink} onClick={() => navigate('/privacy')}>プライバシーポリシー</button>
                    を読み、同意します
                  </span>
                </div>
                <p style={S.authLegal}>
                  {authMode === 'login' ? 'ログイン' : '登録'}前に確認してください。嫌がらせ・不適切な投稿は一切許容されません。
                </p>
                <button data-control="primary" data-control-id="Settings-authSubmitBtn" type="submit" style={S.authSubmitBtn} disabled={authLoading || !authTermsAccepted}>
                  {authLoading ? '処理中…' : authMode === 'login' ? 'ログイン' : '登録する'}
                </button>
              </form>
            </>
          )}
        </section>
      )}

      {!SKIP_API ? (
        <section style={S.section}>
          <h2 style={S.sectionTitle}>プラン</h2>
          <button data-control="row" data-control-id="Settings-linkBtn" type="button" style={S.linkBtn} onClick={() => navigate('/talk/premium')}>
            <span style={S.rowLabel}>トークプレミアム</span>
            <span style={S.rowValue}>{premiumActive ? '利用中' : '未加入'}</span>
            <span style={S.arrow} aria-hidden>›</span>
          </button>
          {/* Guideline 3.1.1: 購入の復元は独立したボタンとして、購読状態に関わらず常に出す */}
          <div style={S.restoreWrap}>
            <RestorePurchasesPanel variant="inline" onRestored={refreshMe} />
          </div>
        </section>
      ) : null}

      <section style={S.section}>
        <h2 style={S.sectionTitle}>表示</h2>
        <div style={S.toggleRow}>
          <div style={S.toggleInfo}>
            <span style={S.toggleLabel}>オンライン表示</span>
            <span style={S.toggleHint}>オンライン状態を他のユーザーに表示</span>
          </div>
          <button data-control="switch" data-control-id="Settings-toggle"
            type="button"
            style={{ ...S.toggle, ...(appearOnline ? S.toggleOn : S.toggleOff) }}
            onClick={handleToggleOnline}
            role="switch"
            aria-checked={appearOnline}
            aria-label="オンライン表示"
          >
            <span style={{ ...S.toggleThumb, ...(appearOnline ? S.thumbOn : {}) }} />
          </button>
        </div>
      </section>

      {!SKIP_API && (
        <section style={S.section}>
          <details>
          <summary><span>興味のあること</span><span style={S.rowValue}>{interests.length}件選択</span></summary>
          <p style={S.sectionDesc}>
            おすすめに反映するカテゴリを5つまで選べます。
          </p>
          <div style={S.interestGrid}>
            {CATEGORIES.map((c) => {
              const active = interests.includes(c.id);
              const disabled = !active && interests.length >= 5;
              return (
                <button data-control="choice" data-control-id="Settings-interestChip"
                  key={c.id}
                  type="button"
                  style={{
                    ...S.interestChip,
                    ...(active ? S.interestChipActive : {}),
                    ...(disabled ? S.interestChipDisabled : {}),
                  }}
                  aria-pressed={active}
                  onClick={() => toggleInterest(c.id)}
                  disabled={disabled}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
          <p style={S.interestCount}>{interests.length} / 5 選択中</p>
          {interestsMsg ? <p style={S.interestsMsg}>{interestsMsg}</p> : null}
          <button data-control="primary" data-control-id="Settings-interestSaveBtn"
            type="button"
            style={S.interestSaveBtn}
            onClick={saveInterests}
            disabled={interestsSaving}
          >
            {interestsSaving ? '保存中…' : '興味のあることを保存'}
          </button>
          </details>
        </section>
      )}

      <section style={S.section}>
        <details>
        <summary><span>背景</span><span style={S.rowValue}>{presets.find(p => p.id === backgroundId)?.label}</span></summary>
        <div style={S.grid}>
          {presets.map((p) => {
            const active = p.id === backgroundId;
            const bg = p.vars['--bg'] || BASE_CSS_VARS['--bg'];
            const surface = p.vars['--surface'] || BASE_CSS_VARS['--surface'];
            return (
              <button data-control="choice" data-control-id="Settings-card"
                key={p.id}
                type="button"
                style={{ ...S.card, ...(active ? S.cardActive : {}) }}
                aria-pressed={active}
                onClick={() => setBackgroundId(p.id)}
              >
                <div style={S.swatchWrap} aria-hidden>
                  <div style={{ ...S.swatchOuter, background: bg }}>
                    <div style={{ ...S.swatchInner, background: surface }} />
                  </div>
                </div>
                <span style={S.cardLabel}>{p.label}</span>

                <span style={{ ...S.check, visibility: active ? 'visible' : 'hidden' }} aria-hidden>✓</span>
              </button>
            );
          })}
        </div>
        </details>
      </section>

      {!SKIP_API && (
        <section style={S.section}>
          <h2 style={S.sectionTitle}>安全とプライバシー</h2>


          <details style={S.safetyCard}>
            <summary>
              <span style={S.safetyCardTitle}>ブロック中のユーザー</span>
              <span style={S.rowValue}>{blockedUsers.length}人</span>
            </summary>
            {!safetyLoaded ? (
              <p style={S.safetyEmpty}>読み込み中…</p>
            ) : blockedUsers.length === 0 ? (
              <p style={S.safetyEmpty}>
                ブロック中のユーザーはいません。
              </p>
            ) : (
              blockedUsers.map(b => (
                <div key={b.id} style={S.blockedRow}>
                  {b.avatar
                    ? <img src={b.avatar} alt="" style={S.blockedAvatar} />
                    : <span style={S.blockedAvatarFallback}>{(b.username || '?')[0].toUpperCase()}</span>}
                  <span style={S.blockedName}>{b.username}</span>
                  <button data-control="secondary" data-control-id="Settings-unblockBtn"
                    type="button"
                    style={S.unblockBtn}
                    onClick={() => unblockUser(b)}
                    disabled={unblockingId === b.id}
                  >
                    {unblockingId === b.id ? '…' : '解除'}
                  </button>
                </div>
              ))
            )}
          </details>

          <details style={S.safetyCard}>
            <summary>
              <span style={S.safetyCardTitle}>通報した内容</span>
              <span style={S.rowValue}>{myReports.length}件</span>
            </summary>
            {!safetyLoaded ? (
              <p style={S.safetyEmpty}>読み込み中…</p>
            ) : myReports.length === 0 ? (
              <p style={S.safetyEmpty}>
                通報した内容はありません。
              </p>
            ) : (
              myReports.slice(0, 20).map(r => (
                <div key={r.id} style={S.reportRow}>
                  <span style={S.reportReason}>{r.reason_label}</span>
                  <span style={S.reportSnapshot}>{r.content_snapshot || '(内容なし)'}</span>
                  <span style={S.reportStatus}>
                    {r.status === 'pending' ? '確認中' : '対応済み'}
                  </span>
                </div>
              ))
            )}
          </details>

          <div style={S.legalRow}>
            <button data-control="row" data-control-id="Settings-legalLink" type="button" style={S.legalLink} onClick={() => navigate('/terms')}>
              利用規約（EULA）
            </button>
            <button data-control="row" data-control-id="Settings-legalLink" type="button" style={S.legalLink} onClick={() => navigate('/privacy')}>
              プライバシーポリシー
            </button>
          </div>
        </section>
      )}

      {/* Guideline 1.5: サポートへの導線をアプリ内からも用意する */}
      <section style={S.section}>
        <h2 style={S.sectionTitle}>ヘルプ</h2>
        <button data-control="row" data-control-id="Settings-supportBtn"
          type="button"
          style={S.supportBtn}
          onClick={() => navigate('/support')}
        >
          <span style={S.supportBtnText}>よくある質問・お問い合わせ</span>
          <span style={S.supportBtnArrow} aria-hidden>›</span>
        </button>
        <a href="mailto:jtalk.se1@gmail.com?subject=J%20TALK%20サポート" style={S.supportMail}>
          メールで問い合わせる
        </a>
      </section>

      {!SKIP_API && (
        <>
          <section style={{ ...S.section, ...S.dangerSection }}>
            <button data-control="row" data-control-id="Settings-logoutBtn" type="button" style={S.logoutBtn} onClick={handleLogout}>
              ログアウト
            </button>
          </section>
          <section style={{ ...S.section, ...S.dangerSection }}>
            <h2 style={S.sectionTitle}>アカウント削除</h2>
            <p style={S.sectionDesc}>
              退会すると投稿・メッセージ・プロフィールなどすべてのデータが完全に削除されます。この操作は取り消せません。
            </p>
            <DeleteAccountPanel />
          </section>
        </>
      )}
    </div>
  );
}

const S = {
  page: { minHeight: '100vh', background: 'var(--surface)', color: 'var(--text)', paddingBottom: 24 },
  header: { display: 'flex', alignItems: 'center', padding: '8px 8px', background: 'var(--surface)', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, zIndex: 10 },
  back: { background: 'none', border: 'none', fontSize: 28, cursor: 'pointer', color: 'var(--text)', width: 44, height: 44, flexShrink: 0 },
  title: { flex: 1, fontSize: 17, fontWeight: 600, textAlign: 'center' },
  headerSpacer: { width: 44 },
  section: { margin: '0 20px', padding: '20px 0', borderBottom: '1px solid var(--border-light)' },
  sectionTitle: { fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 8 },
  sectionDesc: { fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.8, margin: '8px 0 16px' },
  rowLabel: { flex: 1, fontSize: 15, textAlign: 'left' },
  rowValue: { fontSize: 12, fontWeight: 400, color: 'var(--text-secondary)' },
  arrow: { fontSize: 23, color: 'var(--text-secondary)', fontWeight: 400 },
  linkBtn: { display: 'flex', alignItems: 'center', gap: 12, width: '100%', minHeight: 48, padding: 0, border: 0, background: 'transparent', color: 'var(--text)', font: 'inherit', cursor: 'pointer' },
  restoreWrap: {},
  supportBtn: { display: 'flex', alignItems: 'center', gap: 12, width: '100%', minHeight: 48, padding: 0, border: 0, background: 'transparent', textAlign: 'left', font: 'inherit', cursor: 'pointer' },
  supportBtnText: { flex: 1, fontSize: 15, fontWeight: 400, color: 'var(--text)' },
  supportBtnArrow: { fontSize: 23, color: 'var(--text-secondary)' },
  supportMail: { display: 'inline-flex', alignItems: 'center', minHeight: 44, fontSize: 13, color: 'var(--text-secondary)', textUnderlineOffset: 4 },
  toggleRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, minHeight: 56 },
  toggleInfo: { display: 'flex', flexDirection: 'column', gap: 6, flex: 1 },
  toggleLabel: { fontSize: 15, fontWeight: 400 },
  toggleHint: { fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 },
  toggle: { flexShrink: 0, width: 48, height: 28, borderRadius: 14, border: 0, cursor: 'pointer', position: 'relative', transition: 'background .15s', padding: 0 },
  toggleOn: { background: 'var(--primary)' },
  toggleOff: { background: 'var(--border)' },
  toggleThumb: { position: 'absolute', top: 3, left: 3, width: 22, height: 22, borderRadius: '50%', background: '#fff', transition: 'transform .15s', display: 'block' },
  thumbOn: { transform: 'translateX(20px)' },
  interestGrid: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 },
  interestChip: { minHeight: 44, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 400, fontFamily: 'inherit', cursor: 'pointer' },
  interestChipActive: { borderColor: 'var(--primary-dark)', background: 'var(--primary-ultra)', color: 'var(--text)' },
  interestChipDisabled: { opacity: .4, cursor: 'default' },
  interestCount: { margin: '0 0 12px', fontSize: 12, color: 'var(--text-secondary)' },
  interestsMsg: { margin: '0 0 12px', fontSize: 13, color: 'var(--text)' },
  interestSaveBtn: { minHeight: 44, padding: '10px 24px', borderRadius: 8, border: 0, background: 'var(--primary-dark)', color: 'var(--surface)', fontSize: 14, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8, paddingTop: 12 },
  card: { display: 'flex', alignItems: 'center', gap: 8, minHeight: 56, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' },
  cardActive: { borderColor: 'var(--primary-dark)' },
  swatchWrap: { flexShrink: 0 },
  swatchOuter: { width: 26, height: 26, borderRadius: '50%', border: '1px solid var(--border)', display: 'grid', placeItems: 'center' },
  swatchInner: { width: 12, height: 12, borderRadius: '50%' },
  cardLabel: { flex: 1, fontSize: 12, fontWeight: 400, color: 'var(--text)' },
  check: { fontSize: 12, color: 'var(--primary-dark)' },
  accountInfo: { padding: '4px 0' },
  accountMeta: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 },
  accountName: { fontSize: 18, fontWeight: 600, overflowWrap: 'anywhere' },
  accountEmail: { fontSize: 12, color: 'var(--text-secondary)', overflowWrap: 'anywhere' },
  unameRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
  unameEditBtn: { minHeight: 44, padding: '0 8px', background: 'none', border: 0, fontSize: 13, color: 'var(--primary-dark)', cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 },
  unameForm: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 },
  unameInput: { flex: '1 0 100%', minWidth: 0, minHeight: 44, padding: '8px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: 16, fontFamily: 'inherit' },
  unameSave: { minHeight: 44, padding: '0 10px', borderRadius: 8, border: 0, background: 'var(--primary-dark)', color: 'var(--surface)', fontSize: 12, fontFamily: 'inherit', cursor: 'pointer', flexShrink: 0 },
  unameCancel: { minHeight: 44, background: 'none', border: 0, fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'inherit', cursor: 'pointer', flexShrink: 0 },
  unameError: { fontSize: 12, color: 'var(--error)' },
  logoutBtn: { width: '100%', minHeight: 48, padding: 0, border: 0, background: 'transparent', textAlign: 'left', color: 'var(--text)', fontSize: 15, fontWeight: 400, fontFamily: 'inherit', cursor: 'pointer' },
  dangerSection: {},
  guestNote: { fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7, margin: '0 0 16px' },
  authTabs: { display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: 16 },
  authTab: { flex: 1, minHeight: 44, border: 0, background: 'transparent', color: 'var(--text-secondary)', fontSize: 14, fontFamily: 'inherit', cursor: 'pointer' },
  authTabActive: { borderBottom: '2px solid var(--primary-dark)', color: 'var(--text)' },
  authForm: { display: 'flex', flexDirection: 'column', gap: 12 },
  authInput: { minHeight: 48, padding: '12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: 16, fontFamily: 'inherit' },
  authError: { fontSize: 13, color: 'var(--error)', margin: 0 },
  authSubmitBtn: { minHeight: 48, padding: '12px', borderRadius: 8, border: 0, background: 'var(--primary-dark)', color: 'var(--surface)', fontSize: 14, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' },
  authLegal: { margin: 0, fontSize: 12, lineHeight: 1.7, color: 'var(--text-secondary)' },
  authLegalLink: { background: 'none', border: 0, padding: 0, fontFamily: 'inherit', fontSize: 12, color: 'var(--text)', textDecoration: 'underline', textUnderlineOffset: 3, cursor: 'pointer' },
  authTermsCheck: { display: 'flex', alignItems: 'flex-start', gap: 9, color: 'var(--text-secondary)', fontSize: 12, lineHeight: 1.7 },
  authTermsCheckInput: { width: 19, height: 19, margin: '2px 0 0', flex: '0 0 auto', accentColor: 'var(--primary-dark)' },
  safetyCard: { padding: '4px 0' },
  safetyCardTitle: { fontSize: 15, fontWeight: 400 },
  safetyEmpty: { padding: '4px 0 12px', fontSize: 12, lineHeight: 1.7, color: 'var(--text-secondary)' },
  blockedRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0' },
  blockedAvatar: { width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 },
  blockedAvatarFallback: { width: 32, height: 32, borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center', background: 'var(--bg)', color: 'var(--text-secondary)', fontSize: 14 },
  blockedName: { flex: 1, minWidth: 0, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  unblockBtn: { flexShrink: 0, minHeight: 44, padding: '8px 12px', borderRadius: 8, background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-secondary)', fontSize: 12, fontFamily: 'inherit', cursor: 'pointer' },
  reportRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '12px 0' },
  reportReason: { flexShrink: 0, fontSize: 12, color: 'var(--text)' },
  reportSnapshot: { flex: 1, minWidth: 0, fontSize: 12, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  reportStatus: { flexShrink: 0, fontSize: 11, color: 'var(--text-secondary)' },
  legalRow: { display: 'flex', flexDirection: 'column', paddingTop: 8 },
  legalLink: { minHeight: 44, background: 'none', border: 0, padding: 0, textAlign: 'left', fontFamily: 'inherit', fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer' },
};
